// src/components/NewTicketView.tsx

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchFaults, submitTicket, fetchUserTickets, canUserCreateTicketForOthers, fetchFAQ } from "../api";
import type { Fault, Ticket } from "../types";
import FAQs from './FAQ';

import cardStyles from "../styles/Card.module.css";
import buttonStyles from "../styles/Button.module.css";
import formStyles from "../styles/Form.module.css";
import adminStyles from "../styles/AdminView.module.css";
import { GlobalContext } from "../App";
import { useAuth } from "react-oidc-context";
import { AlertModalContext } from "./AlertModal";

export default function NewTicketView() {
  const auth = useAuth();
  const [stage, setStage] = useState<
    "faq" | "choose" | "collectAlpha" | "collectLocation" | "faults" | "submitted"
  >("faq");

  const [forSelf, setForSelf] = useState<boolean | null>(null);
  const [alphaNumber, setAlphaNumber] = useState<string>("");
  const [location, setLocation] = useState<string>("");

  const [faults, setFaults] = useState<Fault[]>([]);
  const [selectedFaultIds, setSelectedFaultIds] = useState<number[]>([]);
  const [newTicketId, setNewTicketId] = useState<number | null>(null);

  const [displayFAQ, setDisplayFAQ] = useState<boolean>(true)

  // alert modal state
  const { setShowAlertModal, setAlertMessage } = useContext(AlertModalContext);

  // modal open/close
  const openAlertModal = (text:string) => {
    setAlertMessage(text);
    setShowAlertModal(true);
  }

  const navigate = useNavigate();

  const { globalSite, setSite } = useContext(GlobalContext);
  const [user, setUser] = useState<string>("");


  useEffect(() => {
    if (typeof auth.user?.profile.site === "string") {
      setSite(auth.user?.profile.site);
    }
    setUser(typeof auth.user?.profile.sentence === "string" ? auth.user.profile.sentence : "");
    fetchFaults()
      .then(setFaults)
      .catch((err) => {
        console.error("Failed to fetch faults:", err);
        openAlertModal("Unable to load issue list. Please refresh the page.");
      });
  }, []);

  useEffect(() => {
    console.log("global", globalSite)
    fetchFAQ(globalSite).then((r) => {
      if (r.length === 0) {
        setDisplayFAQ(false)
      }
    })
  }, [globalSite, displayFAQ, setDisplayFAQ])

  const handleChooseMyself = async () => {
    try {
      const allMyTickets: Ticket[] = await fetchUserTickets(
        user,
        globalSite
      );
      const mySelfTickets = allMyTickets.filter(
        (t) => t.userName === user && t.status === "Requested"
      );
      if (mySelfTickets.length > 0) {
        openAlertModal("You already have a ticket open for yourself.");
        return;
      }
      setForSelf(true);
      setStage("collectLocation");
    } catch (err) {
      console.error("Error checking existing tickets:", err);
      openAlertModal("Unable to verify existing ticket. Please try again.");
    }
  };

  const handleChooseSomeoneElse = async () => {
    try {
      if (!await canUserCreateTicketForOthers(globalSite, user)) {
        openAlertModal("You cannot create any more tickets for other users");
        return;
      }
      //
      setForSelf(false);
      setStage("collectAlpha");
    } catch (err) {
      console.error("Error checking existing tickets:", err);
      openAlertModal("Unable to verify existing ticket for others. Please try again.");
    }
  };

  const handleAlphaContinue = () => {
    if (!alphaNumber.trim()) {
      openAlertModal("Please enter an Alpha Number for the other user.");
      return;
    }
    setStage("collectLocation");
  };

  const handleLocationContinue = () => {
    const trimmed = location.trim();
    if (!trimmed) {
      openAlertModal("Please enter a Cell number.");
      return;
    }
    if (trimmed.length > 6) {
      openAlertModal("Cell number must be at most 6 characters.");
      return;
    }
    setLocation(trimmed);
    setStage("faults");
  };

  const onSubmitFaults = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFaultIds.length === 0) {
      openAlertModal("Please select at least one issue.");
      return;
    }

    const ticketUserId = forSelf ? user : alphaNumber.trim();

    try {
      // If your API supports location, pass it as the last argument.
      const created = await submitTicket(
        globalSite,
        ticketUserId,
        user,
        selectedFaultIds,
        location
      );
      setNewTicketId(created.id);
      setStage("submitted");
    } catch (err) {
      console.error("Error submitting ticket:", err);
      openAlertModal("There was an error creating your ticket. Please try again.");
    }
  };

  // --- RENDERING ---
  if (stage === "faq" && displayFAQ) {
    return (
      <div
        style={{
          backgroundColor: "#DEECF1",
          minHeight: "100vh",
          padding: "24px",
          alignItems: "center",
        }}
      >
        <div
          className={cardStyles.card}
          style={{
            maxWidth: 800,
            padding: "32px",
            textAlign: "center",
            margin: "0 auto",
          }}
        >
          <h2>FAQ</h2>
          <h3>Please read before continuing to raise ticket</h3>
          <FAQs/>
          <div
            style={{ display: "flex", justifyContent: "center", gap: "16px" }}
          >
            <button
              onClick={() => {setStage("choose")}}
              className={`${buttonStyles.button} ${buttonStyles.primary}`}
              style={{ flex: 1 }}
              >
              Continue
            </button>
            <button
              onClick={() => navigate(-1)}
              className={`${buttonStyles.button} ${buttonStyles.primary}`}
              style={{ flex: 1 }}
              >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  } else if (stage === "faq") {
    setStage("choose")
  }

  if (stage === "choose") {
    return (
      <div
        style={{
          backgroundColor: "#DEECF1",
          minHeight: "100vh",
          padding: "24px",
          alignItems: "center",
        }}
      >
        <div
          className={cardStyles.card}
          style={{
            maxWidth: 800,
            padding: "32px",
            textAlign: "center",
            margin: "0 auto",
          }}
        >
          <h2>You are {auth.user?.profile.name} ({user})</h2>
          <p
            style={{
              marginTop: "24px",
              marginBottom: "16px",
              fontSize: "1.1rem",
            }}
          >
            Submit ticket for
          </p>
          <div
            style={{ display: "flex", justifyContent: "center", gap: "16px" }}
          >
            <button
              onClick={handleChooseMyself}
              className={`${buttonStyles.button} ${buttonStyles.primary}`}
              style={{ flex: 1 }}
            >
              Myself
            </button>
            <button
              onClick={handleChooseSomeoneElse}
              className={`${buttonStyles.button} ${buttonStyles.primary}`}
              style={{ flex: 1 }}
            >
              Someone Else
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (stage === "collectAlpha") {
    return (
      <div
        style={{
          backgroundColor: "#DEECF1",
          minHeight: "100vh",
          padding: "24px",
        }}
      >
        <div
          className={cardStyles.card}
          style={{ maxWidth: 800, padding: "32px", textAlign: "center" }}
        >
          <h2>Submit ticket for someone else</h2>
          <p style={{ marginTop: "16px", marginBottom: "24px" }}>
            Please enter that user’s Alpha Number
          </p>
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="alphaInput"
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "1rem",
              }}
            >
              Alpha No:
            </label>
            <input
              id="alphaInput"
              type="text"
              value={alphaNumber}
              onChange={(e) => setAlphaNumber(e.target.value)}
              placeholder="e.g. AH234"
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "1rem",
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            />
          </div>
          <button
            onClick={handleAlphaContinue}
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            style={{ width: "100%", padding: "12px 0" }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (stage === "collectLocation") {
    return (
      <div
        style={{
          backgroundColor: "#DEECF1",
          minHeight: "100vh",
          padding: "24px",
        }}
      >
        <div
          className={cardStyles.card}
          style={{ maxWidth: 800, padding: "32px", textAlign: "center" }}
        >
          <h2>Enter issue Cell Number</h2>
          <p style={{ margin: "16px 0 24px" }}>
            Please enter a Cell number (max 6 characters).
          </p>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={6}
            placeholder="e.g. A1-A"
            style={{
              width: "90%",
              padding: "8px 12px",
              fontSize: "1rem",
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
          />
          <button
            onClick={handleLocationContinue}
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            style={{ marginTop: "24px", width: "100%", padding: "12px 0" }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (stage === "faults") {
    return (
      <div
        style={{
          backgroundColor: "#DEECF1",
          minHeight: "100vh",
          padding: "24px",
        }}
      >
        <div
          className={cardStyles.card}
          style={{ padding: "32px", maxWidth: 800, margin: "0 auto" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <p style={{ fontSize: "1.1rem", margin: 0 }}>
              Please select all relevant issues.
            </p>
            <div style={{ fontSize: "0.9rem", fontStyle: "italic" }}>
              Cell number: <strong>{location}</strong>
            </div>
            {!forSelf && (
              <div style={{ fontSize: "1rem" }}>
                <label style={{ marginRight: "8px", fontWeight: 500 }}>
                  Alpha No:
                </label>
                <span
                  style={{
                    padding: "4px 8px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    backgroundColor: "#fafafa",
                  }}
                >
                  {alphaNumber}
                </span>
              </div>
            )}
          </div>

          <form onSubmit={onSubmitFaults} style={{ marginTop: "24px" }}>
            <fieldset className={formStyles.fieldset}>
              <legend className={formStyles.legend}>Faults</legend>
              <div className={formStyles.checkboxList}>
                {faults.map((f) => (
                  <label
                    key={f.id}
                    style={{ display: "block", marginBottom: "8px" }}
                  >
                    <input
                      type="checkbox"
                      value={f.id}
                      checked={selectedFaultIds.includes(f.id)}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setSelectedFaultIds((prev) =>
                          e.target.checked
                            ? [...prev, val]
                            : prev.filter((x) => x !== val)
                        );
                      }}
                      style={{ marginRight: "8px" }}
                    />
                    {f.description}
                  </label>
                ))}
              </div>
            </fieldset>

            <div style={{ textAlign: "center", marginTop: "24px" }}>
              <button
                type="submit"
                className={`${buttonStyles.button} ${buttonStyles.primary}`}
                style={{ padding: "12px 32px", fontSize: "1rem" }}
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (stage === "submitted") {
    return (
      <div
        style={{
          backgroundColor: "#DEECF1",
          minHeight: "100vh",
          padding: "24px",
        }}
      >
        <div
          className={cardStyles.card}
          style={{ textAlign: "center", padding: "32px", maxWidth: 500 }}
        >
          <h2>Thank you!</h2>
          {newTicketId != null && (
            <p>
              Your ticket <strong>#{newTicketId}</strong> has been created.
            </p>
          )}
          <button
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            onClick={() => navigate(-1)}
            style={{
              marginTop: "16px",
              padding: "10px 24px",
              fontSize: "1rem",
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return null;
}
